import {
  Alert, Icon, Select, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import {
  CATEGORY, CUSTOM_MESSAGE_SUBCATEGORY, HEADER_TYPE, SUBCATEGORY, BUTTON_TYPE,
  createButton, initialFormState,
} from '../state';
import { friendlyError } from './errorMessages';

// Copy + option lists read from gs-ui-templates' Category/index.tsx + config.ts
// (CATEGORY_ITEMS / MARKETING_CATEGORY_ITEMS / UTILITY_CATEGORY_ITEMS / UTILITY_PAYMENT_CATEGORY_ITEMS /
// AUTH_CATEGORY_ITEMS / PASSKEY_CATEGORY_ITEM / ZERO_TAP_CATEGORY_ITEMS), trimmed to only the
// subcategories state.js actually models (isCarousel/isLto/isFlow/isOrderDetails/isCopyCode/
// isAutofill/isZeroTap/isPasskey). product_message/catalog/order_status
// have no selector, no `type` mapping and no state fields anywhere in this form's contract, so
// they are left out rather than offered as a dead end.
// Card icons are the console's own hosted SVGs (config.ts's BRIEFCASE/MEGAPHONE/PASSWORD), rendered via Icon's customPath like sections/Body.js's toolbar icons.
const TEMPLATE_ICON_PREFIX = 'https://gs-upload.gupshup.io/templates/icons/ic_';

const CATEGORY_CARDS = [
  {
    value: CATEGORY.MARKETING,
    label: 'Marketing',
    icon: `${TEMPLATE_ICON_PREFIX}briefcase.svg`,
    description: 'Send promo offers, product offers and more to increase awareness and engagement.',
  },
  {
    value: CATEGORY.UTILITY,
    label: 'Utility',
    icon: `${TEMPLATE_ICON_PREFIX}megaphone.svg`,
    description: 'Send account updates, order updates, alerts and more to share important information.',
  },
  {
    value: CATEGORY.AUTHENTICATION,
    label: 'Authentication',
    icon: `${TEMPLATE_ICON_PREFIX}password.svg`,
    description: 'Send codes that allow your customers to access their accounts.',
  },
];

const SUBCATEGORIES_BY_CATEGORY = {
  [CATEGORY.MARKETING]: [
    {
      value: CUSTOM_MESSAGE_SUBCATEGORY,
      label: 'Custom Message',
      subheader: 'Send promotional offers, announcements and more to increase awareness and engagement.',
    },
    {
      value: SUBCATEGORY.CAROUSEL,
      label: 'Carousel',
      subheader: 'You can send up to 10 carousel cards with images or videos',
    },
    {
      // Console appends " (LTO)" to this title only in the dropdown LIST, never the closed
      // select — see subcategoryListTitle below, which applies the same rule.
      value: SUBCATEGORY.LTO,
      label: 'Limited time offer',
      subheader: 'Display expiration dates and running countdown timers for offer codes',
    },
    {
      value: SUBCATEGORY.FLOW,
      label: 'Flow',
      subheader: 'Send a form to capture customer interests, appointment requests, collect feedback or run surveys.',
    },
    // Console-verbatim label/subheader (gs-ui-templates en.json's call_permission_request /
    // use_the_call_permission_request keys) — offered to every account (product decision
    // 2026-09-24), appended last under both Marketing and Utility (console's own
    // Category/index.tsx:182-197).
    {
      value: SUBCATEGORY.CALL_PERMISSION_REQUEST,
      label: 'Call permission request (CPR)',
      subheader: 'Use the Call Permission Request template to get user consent before initiating a WhatsApp voice call',
    },
  ],
  [CATEGORY.UTILITY]: [
    {
      value: CUSTOM_MESSAGE_SUBCATEGORY,
      label: 'Custom Message',
      subheader: 'Send account updates, order updates, alerts and more to share important information.',
    },
    {
      value: SUBCATEGORY.ORDER_DETAILS,
      label: 'Order Details',
      subheader: 'Send messages with order details and payment options',
    },
    {
      value: SUBCATEGORY.CALL_PERMISSION_REQUEST,
      label: 'Call permission request (CPR)',
      subheader: 'Use the Call Permission Request template to get user consent before initiating a WhatsApp voice call',
    },
  ],
  [CATEGORY.AUTHENTICATION]: [
    {
      value: SUBCATEGORY.COPY_CODE,
      label: 'Copy Code',
      subheader: 'Basic authentication with quick setup. Your customers copy and paste the code into your app.',
    },
    {
      value: SUBCATEGORY.AUTOFILL,
      label: 'Autofill',
      // straight apostrophe here — console's en.json really does mix apostrophe styles between
      // this string and Zero-Tap's below.
      subheader: "When a customer taps the button, the code is sent directly to your app. A copy code message will be sent if autofill isn't possible.",
    },
    {
      value: SUBCATEGORY.PASSKEY,
      label: 'Passkey',
      subheader: 'Passkey lets a user authenticate with their device biometric or screen lock instead of typing an OTP.',
    },
    {
      value: SUBCATEGORY.ZERO_TAP,
      label: 'Zero-Tap',
      // curly apostrophe (’) here, unlike Autofill's above — copied verbatim from console's en.json.
      subheader: 'When zero-tap is turned on, the code will automatically be sent without requiring your customer to tap a button. An autofill or copy code message will be sent if zero-tap isn’t possible.',
    },
  ],
};

const DEFAULT_SUBCATEGORY = {
  [CATEGORY.MARKETING]: CUSTOM_MESSAGE_SUBCATEGORY,
  [CATEGORY.UTILITY]: CUSTOM_MESSAGE_SUBCATEGORY,
  [CATEGORY.AUTHENTICATION]: SUBCATEGORY.COPY_CODE,
};

// Withheld from a NEW template's Template Type select — Flow and Order Details need Meta-side setup this UI has no
// way to configure (docs/TEMPLATES_BACKLOG.md's "Blocked on Meta-side setup" section): Flow needs
// a published Meta Flow plus a flow-list API to build the picker, Order Details needs WhatsApp
// Payments. payload.js also never sends a subcategory on the wire at all (its own comment), so
// picking either here today would silently save a plain custom-message template instead of the
// type the user thought they picked. Same reasoning the docs already give for withholding the
// Flow/Order Details CTA BUTTON types from Buttons.js's Add CTA menu — this is the analogous gap
// one level up, for the template's own category.
//
// Call permission request is withheld too (user decision 2026-09-25): the console only offers it
// when the app's enableVoice config is on (gs-ui-templates Category/index.tsx:186,195), and we
// can't read that flag. payload.js still sends callPermissionRequest for an existing CPR template.
//
// An existing template already saved as one of these (edit/clone prefill — prefill.js sets
// selectedSubcategory to FLOW/ORDER_DETAILS from the button shape, and to CALL_PERMISSION_REQUEST
// from the template) must still show its real type:
// buildSubcategoryOptions below appends the selected value, disabled, when it's missing from the
// offered list — same "withheld for new, preserved for existing" fix already applied to CTA
// button types (Buttons.js's buildTypeOptions) and header types (Header.js's
// buildHeaderTypeOptions).
const WITHHELD_NEW_SUBCATEGORIES = [
  SUBCATEGORY.FLOW, SUBCATEGORY.ORDER_DETAILS, SUBCATEGORY.CALL_PERMISSION_REQUEST,
];

// Console-verbatim (en.json's this_feature_is_supported_only_on_capi) — Carousel is the only
// subcategory this form models that the console marks isCapiEnabled: true (config.ts:71); the
// others that carry the same flag (product_message, order_status) aren't modeled here at all (see
// the file-top comment), so Carousel is the only item that ever needs this tooltip.
const CAPI_ONLY_TEXT = 'Feature supported for only Meta cloud hosted businesses.';

// Console appends " (LTO)" to the dropdown LIST title only (Category/index.tsx:248's header
// ternary) — the closed select stays plain (:284's renderSubCategoryLabel reads the un-suffixed
// .label). Mirrored one level down here: this only affects the rich list item built below;
// renderSubcategoryValue (in the component) reads item.label directly, with no suffix.
function subcategoryListTitle(item) {
  return item.value === SUBCATEGORY.LTO ? `${item.label} (LTO)` : item.label;
}

// Dropdown LIST item: title (+ an info tooltip for Carousel) with its description underneath —
// console's own Category/index.tsx:262-278 nests the same two pieces inside one Select item's
// `label`, so the closed select can stay title-only (see renderSubcategoryValue).
function SubcategoryOptionLabel({ item }) {
  return (
    <div className="br-tpl-form-subcategory-option">
      <div className="br-tpl-form-inline-row">
        <Typography variant="body2">{subcategoryListTitle(item)}</Typography>
        {item.value === SUBCATEGORY.CAROUSEL && (
          <Tooltip
            placement="right"
            title={CAPI_ONLY_TEXT}
            data-testid={`bloomreach-templates-form-category-subcategory-${item.value.toLowerCase()}-capi-tooltip`}
          >
            <span>
              <Icon customPath={`${TEMPLATE_ICON_PREFIX}info_disabled.svg`} size={14} alt="" />
            </span>
          </Tooltip>
        )}
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">{item.subheader}</Typography>
    </div>
  );
}

function buildSubcategoryOptions(subcategoryList, selectedValue) {
  const offered = subcategoryList.filter((item) => !WITHHELD_NEW_SUBCATEGORIES.includes(item.value));
  const toOption = (item, disabled) => ({
    value: item.value,
    label: <SubcategoryOptionLabel item={item} />,
    ...(disabled ? { disabled: true } : {}),
    'data-testid': `bloomreach-templates-form-category-subcategory-${item.value.toLowerCase()}`,
  });
  const options = offered.map((item) => toOption(item, false));
  if (selectedValue && !offered.some((item) => item.value === selectedValue)) {
    const existing = subcategoryList.find((item) => item.value === selectedValue);
    if (existing) {
      options.push(toOption(existing, true));
    }
  }
  return options;
}

// 2. Category — three-card radio + a dependent subcategory Select.
const CategorySection = ({
  state, dispatch, errors, disabled, isEdit,
}) => {
  const { category } = state;

  // Console only unlocks an edited template's category when status === REJECTED AND
  // reason === "Incorrect Category" (C/utils/createTemplateHooks.ts:91-102,110). Our list row
  // carries no rejection reason in form state, so we unlock for ANY REJECTED template instead.
  // Clone is never locked either — isEdit is Boolean(template), which stays false for a clone
  // (Form/index.js only sets cloneSource for that path).
  const categoryLocked = isEdit && state.status.value !== 'REJECTED';

  const handleCategoryChange = (value) => {
    // Mirrors gs-ui-templates Category/index.tsx's handleCategoryChange: a new category
    // invalidates the previous one's header/footer/buttons, so reset them instead of leaving
    // stale data from the old category's shape behind. Setting header.value itself cascades
    // headerText/headerMedia for us — see state.js's formReducer/withHeaderExclusion.
    dispatch({ type: 'set', path: 'header.value', value: HEADER_TYPE.NONE });
    dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
    dispatch({ type: 'set', path: 'footer.footerText', value: { value: '', error: '' } });
    dispatch({ type: 'set', path: 'buttons', value: [] });
    // Body.js hides the body field entirely for non-Passkey Authentication, so nothing else ever
    // clears it — without this, a Marketing/Utility body typed before switching TO Authentication
    // rides along as `template` on save (payload.js), and symmetrically for a body typed while on
    // Passkey when switching AWAY from Authentication. Console only resets on the "leaving auth"
    // half (Category/index.tsx:104-106) because its own Body/index.tsx separately overwrites the
    // body with server text on entry (AUTH_BODY) — this form never shows or sends that text at
    // all (see prefill.js/payload.js), so both directions need the same explicit reset here.
    if (category.value === CATEGORY.AUTHENTICATION || value === CATEGORY.AUTHENTICATION) {
      dispatch({ type: 'set', path: 'body', value: initialFormState().body });
    }
    // Console resets unconditionally on every category change (Category/index.tsx:125) — the
    // security-recommendation/expiration checkboxes have no reset of their own otherwise, so a
    // flag ticked under Authentication would otherwise survive a switch away and back.
    dispatch({ type: 'set', path: 'messageContent', value: initialFormState().messageContent });
    // Marketing-only flag (state.js) — a category change already wipes buttons/footer above, which
    // is exactly what the flag would otherwise dangle a stale reference to. Reset unconditionally,
    // same as every other field this handler resets regardless of which category was just picked.
    dispatch({ type: 'set', path: 'optOutButton', value: false });
    // Each category offers its own messageValidity option list (sections/MessageValidity.js:
    // AUTH_MESSAGE_VALIDITY tops out at 15 minutes, MARKETING_MESSAGE_VALIDITY goes to 30 days) —
    // reset it too, or a value legal in the old category's dropdown rides along as one the new
    // category's dropdown never offers.
    dispatch({
      type: 'set',
      path: 'messageValidity',
      value: { validityPeriod: { value: '', error: '' }, isValidityChecked: false },
    });
    // carousels.cards is Marketing/Carousel-only. Reset it here too, or leaving the category and
    // later coming back to Marketing > Carousel resurfaces old cards/media untouched.
    dispatch({
      type: 'set',
      path: 'carousels',
      value: { mediaType: 'IMAGE', buttonTypes: [], cards: [] },
    });
    dispatch({
      type: 'set',
      path: 'category',
      value: {
        value,
        error: '',
        selectedSubcategory: { value: DEFAULT_SUBCATEGORY[value] || '', error: '' },
      },
    });
  };

  const handleSubCategoryChange = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    const previousSubcategory = category.selectedSubcategory.value;
    dispatch({ type: 'set', path: 'category.selectedSubcategory', value: { value, error: '' } });
    // Console resets LTO's own fields on every subcategory change, unconditionally — see
    // Category/index.tsx handleSubCategoryChange's final resetCreateTemplateField('lto').
    dispatch({
      type: 'set',
      path: 'lto',
      value: { offerTitle: { value: '', error: '' }, isExpiryChecked: false },
    });
    // carousels.cards is Carousel-only — same unconditional reset as lto above, so leaving
    // Carousel for another subcategory and later switching back doesn't resurface old
    // cards/media untouched.
    dispatch({
      type: 'set',
      path: 'carousels',
      value: { mediaType: 'IMAGE', buttonTypes: [], cards: [] },
    });
    // Passkey is the only Authentication sub-type with an editable body/footer (Body.js/Footer.js
    // hide both for copy_code/autofill/zero_tap) — moving into or out of it must not let either
    // carry over to a sibling sub-type. Console references: Body/index.tsx:66-75 (its own
    // isPasskey effect resets the body), Category/index.tsx:104-106 (the analogous category-level
    // reset this mirrors one level down).
    if (previousSubcategory === SUBCATEGORY.PASSKEY || value === SUBCATEGORY.PASSKEY) {
      dispatch({ type: 'set', path: 'body', value: initialFormState().body });
      dispatch({ type: 'set', path: 'footer.footerText', value: initialFormState().footer.footerText });
    }
    // 'CAROUSEL' has no meaning as a wire `type` outside an actual carousel template
    // (MessageTemplate.java's isCarouselTemplate() checks type === CAROUSEL) — leaving Carousel
    // for anything else must not let it silently ride along (T1). Skipped when re-entering
    // Carousel below, which sets its own correct value anyway.
    if (previousSubcategory === SUBCATEGORY.CAROUSEL && value !== SUBCATEGORY.CAROUSEL) {
      dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
    }
    if (value === SUBCATEGORY.CAROUSEL) {
      // Carousel has no regular header or footer in WhatsApp's spec — it builds its own
      // header-shaped fields (carousels.mediaType, out of scope here) instead. Enterprise only
      // treats the template as a carousel when the wire `type` is literally CAROUSEL
      // (MessageTemplate.java:2996-3002) — nothing else ever sets this for a NEW template, so a
      // fresh carousel used to silently save as a plain TEXT template (T1).
      dispatch({ type: 'set', path: 'header.value', value: HEADER_TYPE.NONE });
      dispatch({ type: 'set', path: 'type.value', value: 'CAROUSEL' });
      dispatch({ type: 'set', path: 'footer.footerText', value: { value: '', error: '' } });
      // Neither subcategory offers the footer the opt-out flag depends on (sections/Buttons.js's
      // optOutOfferable gates on !isLto(state); Carousel's own Buttons section never renders at
      // all) — reset it here too, or it can linger true after either switch.
      dispatch({ type: 'set', path: 'optOutButton', value: false });
      // Buttons.js hides its whole section for Carousel (isCarousel short-circuits it), so any
      // top-level buttons already on state would sit invisible yet still ride along into
      // payload.js's quickReplyButtons/callToActionButtons — clear them on this switch too, same
      // as the LTO reset below.
      dispatch({ type: 'set', path: 'buttons', value: [] });
    } else if (value === SUBCATEGORY.LTO) {
      dispatch({ type: 'set', path: 'header.value', value: HEADER_TYPE.NONE });
      dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
      dispatch({ type: 'set', path: 'footer.footerText', value: { value: '', error: '' } });
      dispatch({ type: 'set', path: 'optOutButton', value: false });
      // Enterprise requires at least one URL CTA on an LTO template ("Please pass at least 1
      // website button", MessageTemplate.java:1781-1785) — seed one via the normal factory,
      // locked so Buttons.js won't offer to retype/remove/drag it (T6). The expiry checkbox
      // (Lto.js) inserts/removes a second locked COPY_CODE button in front of this one.
      dispatch({ type: 'set', path: 'buttons', value: [createButton(BUTTON_TYPE.URL, { locked: true })] });
    } else if (value === SUBCATEGORY.CALL_PERMISSION_REQUEST) {
      // Console resets header and buttons on selecting Call permission request
      // (Category/index.tsx:167-170) — Header.js only offers None/Text for it (Enterprise
      // requires type TEXT, ValidationUtils.java:734-749) and Buttons.js hides its whole section
      // for it, so any previously-set media header or CTA/quick-reply buttons must not silently
      // ride along.
      dispatch({ type: 'set', path: 'header.value', value: HEADER_TYPE.NONE });
      dispatch({ type: 'set', path: 'type.value', value: 'TEXT' });
      dispatch({ type: 'set', path: 'buttons', value: [] });
      // Buttons.js hides its whole section for CPR too (same as Carousel/LTO above), so a
      // previously-checked opt-out flag would otherwise linger true with no checkbox left to
      // untick it — same reset, same reason as the Carousel/LTO branch's own optOutButton line.
      dispatch({ type: 'set', path: 'optOutButton', value: false });
    } else if (value === CUSTOM_MESSAGE_SUBCATEGORY) {
      // Console resets buttons when switching to either category's plain "custom message" type
      // (Category/index.tsx:154-159) — otherwise LTO's own locked buttons (or Carousel/CPR
      // leftovers) leak into a template that renders a completely different Buttons section (T6).
      dispatch({ type: 'set', path: 'buttons', value: [] });
    }
  };

  const subcategoryList = SUBCATEGORIES_BY_CATEGORY[category.value] || [];
  const subcategoryItems = buildSubcategoryOptions(subcategoryList, category.selectedSubcategory.value);
  // Closed select: title only, no caption/suffix — console's renderSubCategoryLabel
  // (Category/index.tsx:283-290) reads the same plain .label this looks up here.
  const renderSubcategoryValue = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    const item = subcategoryList.find((entry) => entry.value === value);
    return item ? item.label : '';
  };

  const isUtilityCustomMessage = category.value === CATEGORY.UTILITY
    && category.selectedSubcategory.value === CUSTOM_MESSAGE_SUBCATEGORY;
  const isAuthPasskey = category.value === CATEGORY.AUTHENTICATION
    && category.selectedSubcategory.value === SUBCATEGORY.PASSKEY;
  const isAuthAutofillOrZeroTap = category.value === CATEGORY.AUTHENTICATION
    && (category.selectedSubcategory.value === SUBCATEGORY.AUTOFILL
      || category.selectedSubcategory.value === SUBCATEGORY.ZERO_TAP);

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-category">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-category-heading">
            Category*
          </Typography>
        </div>
        <Tooltip
          placement="left"
          title="These are categories defined by WhatsApp required to be submitted"
          data-testid="bloomreach-templates-form-category-tooltip"
        >
          <span>
            <Icon name="question" size={18} showCursor />
          </span>
        </Tooltip>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Choose what type of message template you want to create.
      </Typography>

      <div
        className="br-tpl-form-category-grid"
        role="radiogroup"
        data-testid="bloomreach-templates-form-category-radio"
      >
        {CATEGORY_CARDS.map((item) => (
          <label
            key={item.value}
            className={
              category.value === item.value
                ? 'br-tpl-form-category-card br-tpl-form-category-card--selected'
                : 'br-tpl-form-category-card'
            }
          >
            <div className="br-tpl-form-category-card-head">
              <input
                type="radio"
                name="bloomreach-template-category"
                className="br-tpl-form-category-radio-input"
                value={item.value}
                checked={category.value === item.value}
                onChange={() => handleCategoryChange(item.value)}
                disabled={disabled || categoryLocked}
                data-testid={`bloomreach-templates-form-category-${item.value.toLowerCase()}`}
              />
              <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
              <Icon customPath={item.icon} size={20} alt="" />
            </div>
            <Typography variant="caption" className="br-tpl-form-helper-text">{item.description}</Typography>
          </label>
        ))}
      </div>
      {errors['category.value'] && (
        <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-category-error">
          {friendlyError(errors['category.value'])}
        </Typography>
      )}

      {isUtilityCustomMessage && (
        <Alert
          type="warning"
          description="Meta may automatically reclassify or approve templates as Marketing based on content. Always check the updated category before sending to avoid any issues."
          data-testid="bloomreach-templates-form-category-warning-utility"
        />
      )}

      {category.value && (
        <div className="br-tpl-form-subcategory">
          <Select
            label="Template Type"
            items={subcategoryItems}
            value={category.selectedSubcategory.value}
            handleChange={handleSubCategoryChange}
            customRenderValue={renderSubcategoryValue}
            disabled={disabled || categoryLocked}
            width="100%"
            data-testid="bloomreach-templates-form-category-subcategory-select"
          />
          {errors['category.selectedSubcategory.value'] && (
            <Typography
              variant="caption"
              color="error"
              data-testid="bloomreach-templates-form-category-subcategory-error"
            >
              {friendlyError(errors['category.selectedSubcategory.value'])}
            </Typography>
          )}
        </div>
      )}

      {isAuthPasskey && (
        <Alert
          type="warning"
          description="Requires WhatsApp Android v2.25.27.10+ or iOS v2.25.26.21+. Desktop and Web are not supported — unsupported recipients will get a dropped message and a 131026 status webhook."
          data-testid="bloomreach-templates-form-category-warning-auth"
        />
      )}
      {isAuthAutofillOrZeroTap && (
        <Alert
          type="warning"
          description="Zero-tap and auto-fill template type will require OTP Android SDK integration after October 15, 2026. No migration may lead to “Copy Code” fallback. Click here to"
          linkText="learn more"
          // Console's link ends in a pasted `#u[…]k` fragment (config.ts:398) — that's not a real
          // anchor, so it's dropped rather than copied forward.
          linkTo="https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/authentication-templates/zero-tap-authentication-templates"
          data-testid="bloomreach-templates-form-category-warning-auth"
        />
      )}
    </div>
  );
};

export default CategorySection;
